<template>
  <v-layout>
    <v-dialog v-bind:value="dialog" @click:outside="closeDialog()" @keydown.esc="closeDialog()" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-form ref="form" v-on:submit.prevent="importAll">
          <v-card-title>
            <span class="headline">Carga masiva de recibos</span>
          </v-card-title>

          <v-card-text style="margin-bottom: 120px">
            <v-row align="center" justify="center" class="mb-4 mt-4">
              <v-col md="10" class="pa-0">
                <v-stepper :value="step + 1">
                  <v-stepper-header>
                    <v-stepper-step :complete="step > 0" step="1">{{ steps[0] }}</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step step="2">{{ steps[1] }}</v-stepper-step>
                  </v-stepper-header>
                </v-stepper>
              </v-col>
            </v-row>

            <!--step 0-->
            <v-row align="start" justify="center" v-if="step == 0">
              <v-col cols="12" md="5" class="mt-4 text-left">
                <p class="fs-16 mb-2">
                  <b>{{ steps[0] }}</b>
                </p>
                <p class="mb-5">
                  <v-label>
                    Suba el PDF con todos los recibos del período tal como lo exporta el sistema de sueldos, o varios PDF
                    sueltos. En el siguiente paso se revisa a qué empleado corresponde cada recibo antes de importarlo.
                  </v-label>
                </p>

                <div class="bu-section-title">1 · Datos comunes a todos los recibos</div>
                <PayslipPeriodFields v-model="period" />

                <div class="bu-section-title mt-2">2 · Archivos PDF</div>
                <PdfDropZone v-model="files" multiple :error="files_error"
                  hint="Puede ser un PDF con todos los recibos del período o uno por empleado. En el siguiente paso revisás a quién corresponde cada uno." />
                <span v-if="files_error" class="red--text">Elegí al menos un PDF.</span>

                <ul v-if="files.length > 0" class="bu-files">
                  <li v-for="(file, index) in files" :key="file.name + file.size">
                    <v-icon small color="#a8201a">mdi-file-pdf-box</v-icon>
                    <span class="bu-files__name">{{ file.name }}</span>
                    <v-btn icon small :aria-label="'Quitar ' + file.name" @click="removeFile(index)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </li>
                </ul>
              </v-col>
            </v-row>

            <!--step 1-->
            <v-row align="start" justify="center" v-if="step == 1">
              <v-col md="10" class="mt-4">
                <p class="fs-16 mb-2">
                  <b>{{ steps[1] }}</b>
                  <span class="ml-2 grey--text">{{ periodText }}</span>
                </p>

                <div v-if="analyzing">
                  <v-progress-linear :value="analyzePercent" color="blue-grey" height="25" class="mb-3 mt-3">
                    <strong>Analizando {{ analyzed }} de {{ files.length }} archivos</strong>
                  </v-progress-linear>
                </div>
                <v-progress-linear v-else :value="importPercent" color="blue-grey" height="25" class="mb-3 mt-3">
                  <strong>{{ Math.ceil(importPercent) || 0 }}%</strong>
                </v-progress-linear>

                <div class="bu-main">

              <div class="bu-toolbar">
                <div class="bu-tabs" role="tablist" aria-label="Filtrar recibos">
                  <button v-for="tab in visibleTabs" :key="tab.id" type="button" role="tab" class="bu-tab" :aria-selected="String(filter == tab.id)"
                    @click="filter = tab.id">
                    {{ tab.name }} <b>{{ tabCount(tab.id) }}</b>
                  </button>
                </div>
                <v-btn v-if="ai_enabled && aiCandidates.length > 0" outlined color="purple" height="44" :loading="ai_running"
                  :disabled="importing || analyzing" @click="readAllWithAi">
                  <v-icon left>mdi-robot-outline</v-icon> Leer con IA ({{ aiCandidates.length }})
                </v-btn>
              </div>

              <div class="bu-tablewrap">
                <div class="bu-table">
                  <div class="bu-row bu-row--head">
                    <div>Recibo</div>
                    <div>Empleado</div>
                    <div>Estado</div>
                    <div>Importar</div>
                  </div>

                  <v-data-iterator :items="visibleRows" item-key="key" :items-per-page.sync="items_per_page" :page.sync="page"
                    :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }" :hide-default-footer="visibleRows.length <= 25">
                    <template v-slot:default="props">
                      <div v-for="item in props.items" :key="item.key" class="bu-row bu-row--item">
                        <div class="bu-file">
                          <a v-if="!item.error" @click="openPreview(item)">{{ item.file_name }}</a>
                          <span v-else class="bu-file-name">{{ item.file_name }}</span>
                          <span class="bu-muted">
                            {{ item.cuil ? "CUIL " + item.cuil : "CUIL no leído" }}<span v-if="pagesLabel(item)"> · {{ pagesLabel(item) }}</span>
                            <v-chip v-if="item.extraction_method == 'ai'" x-small color="purple lighten-4" text-color="purple darken-3" class="ml-1">IA</v-chip>
                          </span>
                        </div>
                        <div>
                          <v-autocomplete v-if="!item.error && item.status != 'imported'" v-model="item.legajo_id" :items="legajos"
                            :item-text="legajoText" item-value="id" placeholder="Elegir empleado" :disabled="importing || item.ai_loading"
                            outlined dense clearable hide-details class="bu-select" @change="onLegajoChange(item)"></v-autocomplete>
                          <span v-else-if="item.status == 'imported'">{{ legajoName(item.legajo_id) }}</span>
                        </div>
                        <div class="bu-status">
                          <span class="bu-badge" :class="'bu-badge--' + rowBadge(item).kind"><i></i>{{ rowBadge(item).label }}</span>
                          <span v-if="rowBadge(item).reason" class="bu-muted">{{ rowBadge(item).reason }}</span>
                        </div>
                        <div class="bu-check">
                          <v-simple-checkbox :value="item.include && isImportable(item)" :disabled="!canToggle(item)" :ripple="false"
                            @input="(value) => (item.include = value)"></v-simple-checkbox>
                        </div>
                      </div>
                    </template>
                    <template v-slot:no-data>
                      <div class="bu-empty">{{ analyzing ? "Analizando..." : "No hay recibos en este filtro." }}</div>
                    </template>
                  </v-data-iterator>
                </div>
              </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="pa-0" style="position: fixed; bottom: 0; right: 0; left: 0">
            <v-row align="start" justify="center" no-gutters>
              <v-col cols="10" class="py-3" style="background-color: white">
                <v-btn @click="prev" color="blue-grey" outlined v-if="step > 0" :disabled="busy || imported_count > 0">Atras</v-btn>
                <div class="float-right mr-2" v-if="step == 1">
                  <v-chip color="success" class="mr-2">Importados: {{ imported_count }}</v-chip>
                  <v-chip class="mr-2">Omitidos: {{ skipped_count }}</v-chip>
                  <v-chip color="error">Fallidos: {{ failed_count }}</v-chip>
                </div>

                <v-btn class="float-right" outlined color="primary" @click="analyzeFiles" v-if="step == 0">Siguiente</v-btn>
              </v-col>
              <v-col cols="12" class="text-right pa-2" style="background-color: #fafafa; border-top: 1px solid #c1c1c1">
                <v-btn color="error" text @click="closeDialog()" :disabled="busy">Cerrar</v-btn>
                <v-btn color="success" text type="submit" :disabled="analyzing || ai_running || importableRows.length == 0"
                  :loading="importing" v-if="step == 1">Importar ({{ importableRows.length }})</v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <ModalPdfPreview :dialog="dialog_preview" :loader="previewLoader" :file_name="preview_name"
      @updateDialog="(value) => (dialog_preview = value)" />
  </v-layout>
</template>

<script>
import { payslipService } from "@/libs/ws/payslipService";
import { legajoService } from "@/libs/ws/legajoService";
import { PAYSLIP_TYPES, PAYSLIP_TYPE_BIWEEKLY, legajoLabel, periodTitle, formatDate } from "@/constants/payslips";
import PayslipPeriodFields from "./PayslipPeriodFields.vue";
import ModalPdfPreview from "./ModalPdfPreview.vue";
import PdfDropZone from "./PdfDropZone.vue";

const STATUS_PENDING = "pending";
const STATUS_IMPORTED = "imported";
const STATUS_SKIPPED = "skipped";
const STATUS_FAILED = "failed";

// Pestañas del paso 2
const TAB_ALL = "all";
const TAB_READY = "ready";
const TAB_UNMATCHED = "unmatched";
const TAB_OMITTED = "omitted";
const TAB_IMPORTED = "imported";

const defaultPeriod = () => ({
  type: "monthly",
  period_number: null,
  period_month: null,
  period_year: new Date().getFullYear(),
  payment_date: null,
});

export default {
  components: { PayslipPeriodFields, ModalPdfPreview, PdfDropZone },
  props: {
    dialog: Boolean,
  },
  data() {
    return {
      step: 0,
      steps: ["Archivos y período", "Detalle de los recibos"],
      tabs: [
        { id: TAB_ALL, name: "Todos" },
        { id: TAB_READY, name: "Listos" },
        { id: TAB_UNMATCHED, name: "Sin empleado" },
        { id: TAB_OMITTED, name: "Omitidos" },
        { id: TAB_IMPORTED, name: "Importados" },
      ],
      filter: TAB_ALL,
      page: 1,
      items_per_page: 25,
      period: defaultPeriod(),
      files: [],
      files_error: false,
      token: null,
      rows: [],
      legajos: [],
      legajos_with_payslip: [],
      ai_enabled: false,
      analyzing: false,
      analyzed: 0,
      ai_running: false,
      importing: false,
      import_total: 0,
      imported_count: 0,
      skipped_count: 0,
      failed_count: 0,
      dialog_preview: false,
      preview_row: null,
    };
  },
  computed: {
    busy() {
      return this.analyzing || this.importing || this.ai_running;
    },
    periodText() {
      const type = PAYSLIP_TYPES.find((t) => t.id == this.period.type);
      return (type ? type.name : "") + " · " + periodTitle(this.period) + " · Pago " + formatDate(this.period.payment_date);
    },
    analyzePercent() {
      return this.files.length == 0 ? 0 : (this.analyzed * 100) / this.files.length;
    },
    importPercent() {
      if (this.import_total == 0) return 0;
      return ((this.imported_count + this.skipped_count + this.failed_count) * 100) / this.import_total;
    },
    importableRows() {
      return this.rows.filter((row) => row.include && this.isImportable(row));
    },
    unmatchedCount() {
      return this.rows.filter((row) => !row.error && row.status != STATUS_IMPORTED && !row.legajo_id).length;
    },
    aiCandidates() {
      return this.rows.filter((row) => this.canReadWithAi(row));
    },
    // "Importados" aparece recien cuando hay alguno
    visibleTabs() {
      return this.tabs.filter((tab) => tab.id != TAB_IMPORTED || this.tabCount(TAB_IMPORTED) > 0);
    },
    visibleRows() {
      if (this.filter == TAB_ALL) return this.rows;
      return this.rows.filter((row) => this.rowTab(row) == this.filter);
    },
    // Legajo -> clave de la primera fila incluida que lo usa, para marcar los repetidos dentro de la carga
    firstRowByLegajo() {
      const result = {};
      if (this.period.type == "adjustment") return result;
      this.rows.forEach((row) => {
        if (row.legajo_id && (row.include || row.status == STATUS_IMPORTED) && !(row.legajo_id in result)) result[row.legajo_id] = row.key;
      });
      return result;
    },
    previewLoader() {
      if (!this.preview_row) return null;
      const key = this.preview_row.key;
      return () => payslipService.analyze_file(this.token, key);
    },
    preview_name() {
      return this.preview_row ? this.preview_row.file_name + " (" + this.pagesLabel(this.preview_row) + ")" : "";
    },
  },
  watch: {
    files(value) {
      if (value.length > 0) this.files_error = false;
    },
    dialog(value) {
      if (value && this.legajos.length == 0) this.loadLegajos();
    },
  },
  methods: {
    loadLegajos() {
      legajoService
        .filter({ status: "all" })
        .then((response) => {
          if (response.status) this.legajos = response.result;
        })
        .catch((e) => console.log(e));
    },
    legajoText(legajo) {
      return legajoLabel(legajo) + (legajo.cuil ? " (" + legajo.cuil + ")" : "");
    },
    legajoName(id) {
      const legajo = this.legajos.find((l) => l.id == id);
      return legajo ? legajoLabel(legajo) : "-";
    },
    pagesLabel(row) {
      if (row.page_from == null) return "";
      if (row.page_from == 1 && row.page_to == row.file_pages) return row.file_pages == 1 ? "1 página" : row.file_pages + " páginas";
      return row.page_from == row.page_to ? "Página " + row.page_from : "Páginas " + row.page_from + "-" + row.page_to;
    },
    // Aviso que impide importar la fila, o null
    warning(row) {
      if (row.error) return row.error;
      if (!row.legajo_id) return "Sin empleado";
      if (this.legajos_with_payslip.includes(row.legajo_id)) return "Omitido: ya tiene un recibo de este período";
      const first = this.firstRowByLegajo[row.legajo_id];
      if (first != null && first != row.key) return "Omitido: el empleado ya está en otra fila de la carga";
      return null;
    },
    isImportable(row) {
      return row.status != STATUS_IMPORTED && row.status != STATUS_SKIPPED && this.warning(row) == null;
    },
    // Solo se puede destildar/tildar una fila que se podria importar
    canToggle(row) {
      return !this.importing && row.status != STATUS_IMPORTED && row.status != STATUS_SKIPPED && this.warning(row) == null;
    },
    canReadWithAi(row) {
      return !row.error && !row.legajo_id && !row.ai && row.status == STATUS_PENDING;
    },
    // Pestaña en la que aparece la fila
    rowTab(row) {
      if (row.status == STATUS_IMPORTED) return TAB_IMPORTED;
      if (row.include && this.isImportable(row)) return TAB_READY;
      if (!row.error && !row.legajo_id) return TAB_UNMATCHED;
      return TAB_OMITTED;
    },
    tabCount(tab) {
      if (tab == TAB_ALL) return this.rows.length;
      return this.rows.filter((row) => this.rowTab(row) == tab).length;
    },
    // Badge de estado: kind (color), label y motivo
    rowBadge(row) {
      if (row.loading) return { kind: "ready", label: "Importando...", reason: "" };
      if (row.ai_loading) return { kind: "unmatched", label: "Leyendo con IA...", reason: "" };
      if (row.status == STATUS_IMPORTED) return { kind: "imported", label: "Importado", reason: "Quedó en borrador" };
      if (row.status == STATUS_FAILED) return { kind: "error", label: "Error", reason: row.msg };
      if (row.status == STATUS_SKIPPED) return { kind: "omitted", label: "Omitido", reason: row.msg.replace(/^Omitido: /, "") };
      if (row.error) return { kind: "error", label: "No se pudo leer", reason: row.error };
      if (!row.legajo_id) {
        let reason = this.ai_enabled ? "Elegí uno o usá la IA" : "Elegí uno";
        if (row.ai) reason = row.ai.employee_name ? "IA: " + row.ai.employee_name : "La IA no lo encontró. Elegí uno";
        return { kind: "unmatched", label: "Sin empleado", reason: reason };
      }
      const warning = this.warning(row);
      if (warning) return { kind: "omitted", label: "Omitido", reason: warning.replace(/^Omitido: /, "") };
      if (!row.include) return { kind: "omitted", label: "Omitido", reason: "No se importa" };
      return { kind: "ready", label: "Listo", reason: "" };
    },
    onLegajoChange(row) {
      // Al elegir un empleado se vuelve a incluir; un reintento parte de cero
      row.include = true;
      if (row.status == STATUS_FAILED) row.status = STATUS_PENDING;
    },
    openPreview(row) {
      this.preview_row = row;
      this.dialog_preview = true;
    },
    toRow(row) {
      const existing = this.rows.find((r) => r.key == row.key);
      if (existing) return existing;
      return Object.assign({}, row, {
        include: true,
        status: row.payslip_id ? STATUS_IMPORTED : STATUS_PENDING,
        msg: "",
        loading: false,
        ai_loading: false,
      });
    },
    removeFile(index) {
      this.files = this.files.filter((file, i) => i != index);
    },
    async analyzeFiles() {
      const valid = await this.$validator.validate();
      this.files_error = this.files.length == 0;
      if (!valid || this.files_error) return;

      this.step++;
      this.rows = [];
      this.token = null;
      this.analyzed = 0;
      this.analyzing = true;

      for (const file of this.files) {
        const formData = new FormData();
        formData.append("file", file);
        if (this.token) {
          formData.append("token", this.token);
        } else {
          formData.append("type", this.period.type);
          formData.append("period_month", this.period.period_month);
          formData.append("period_year", this.period.period_year);
          formData.append("payment_date", this.period.payment_date);
          if (this.period.type == PAYSLIP_TYPE_BIWEEKLY) formData.append("period_number", this.period.period_number);
        }

        try {
          const response = await payslipService.analyze(formData);
          if (response.status) {
            this.token = response.result.token;
            this.legajos_with_payslip = response.result.legajos_with_payslip;
            this.ai_enabled = response.result.ai_enabled;
            this.rows = response.result.rows.map((row) => this.toRow(row));
          } else {
            this.$toast.error(file.name + ": " + response.msg, "Error");
            // Sin token la carga no se creo: no tiene sentido seguir con el resto
            if (!this.token) break;
          }
        } catch (e) {
          console.log(e);
          this.$toast.error(file.name + ": no se pudo analizar el archivo.", "Error");
          if (!this.token) break;
        }
        this.analyzed++;
      }

      this.analyzing = false;
      if (!this.token) this.step = 0;
    },
    async readWithAi(row) {
      row.ai_loading = true;
      try {
        const response = await payslipService.analyze_ai({ token: this.token, key: row.key });
        if (response.status) {
          const result = response.result;
          row.ai = result.ai;
          row.cuil = result.cuil;
          row.extraction_method = result.extraction_method;
          if (!row.legajo_id && result.legajo_id) row.legajo_id = result.legajo_id;
        } else {
          this.$toast.error(response.msg, "Error");
        }
        return response.status;
      } catch (e) {
        console.log(e);
        this.$toast.error("No se pudo leer el recibo con IA.", "Error");
        return false;
      } finally {
        row.ai_loading = false;
      }
    },
    async readAllWithAi() {
      this.ai_running = true;
      let found = 0;
      for (const row of this.aiCandidates) {
        if (!(await this.readWithAi(row))) break;
        if (row.legajo_id) found++;
      }
      this.ai_running = false;
      this.$toast.info("La IA encontró el empleado de " + found + " recibos.", "IA");
    },
    async importAll() {
      const rows = this.importableRows;
      if (rows.length == 0) return;

      this.importing = true;
      this.import_total = rows.length;
      this.imported_count = 0;
      this.skipped_count = 0;
      this.failed_count = 0;

      for (const row of rows) {
        row.loading = true;
        try {
          const response = await payslipService.import_line({ token: this.token, key: row.key, legajo_id: row.legajo_id });
          if (response.status) {
            row.status = STATUS_IMPORTED;
            this.imported_count++;
          } else if (response.result && response.result.skipped) {
            row.status = STATUS_SKIPPED;
            row.msg = response.msg;
            this.skipped_count++;
          } else {
            row.status = STATUS_FAILED;
            row.msg = response.msg || "Error en la importación";
            this.failed_count++;
          }
        } catch (e) {
          row.status = STATUS_FAILED;
          row.msg = (e && e.msg) || "Error en la importación";
          this.failed_count++;
        }
        row.loading = false;
      }

      this.importing = false;
      if (this.failed_count == 0 && this.importableRows.length == 0 && this.unmatchedCount == 0) {
        this.$toast.success("Se importaron " + this.imported_count + " recibos en borrador.", "OK");
        this.closeDialog();
      }
    },
    closeDialog() {
      if (this.busy) return;
      const has_imported = this.rows.some((row) => row.status == STATUS_IMPORTED);
      this.$emit("updateDialog", false);
      if (has_imported) this.$emit("imported");
      this.resetForm();
    },
    resetForm() {
      this.step = 0;
      this.period = defaultPeriod();
      this.files = [];
      this.files_error = false;
      this.token = null;
      this.rows = [];
      this.legajos_with_payslip = [];
      this.filter = TAB_ALL;
      this.page = 1;
      this.analyzed = 0;
      this.import_total = 0;
      this.imported_count = 0;
      this.skipped_count = 0;
      this.failed_count = 0;
      this.$validator.reset();
    },
    prev() {
      this.step--;
      this.rows = [];
      this.token = null;
    },
  },
};
</script>

<style scoped>
.bu-section-title {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4d6269;
}
.bu-files {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  border: 1px solid #cfd9dc;
  border-radius: 8px;
  overflow: hidden;
}
.bu-files li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 8px 6px 14px;
  border-bottom: 1px solid #e3eaec;
}
.bu-files li:last-child {
  border-bottom: none;
}
.bu-files__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #1d2b30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bu-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.bu-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.bu-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.bu-tab {
  height: 44px;
  padding: 0 16px;
  border-radius: 22px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #b8c4c9;
  background: #fff;
  color: #24485a;
}
.bu-tab[aria-selected="true"] {
  background: #24485a;
  border-color: #24485a;
  color: #fff;
}
.bu-tab b {
  font-weight: 600;
}
.bu-tablewrap {
  overflow-x: auto;
  border: 1px solid #c9d3d7;
  border-radius: 6px;
}
.bu-table {
  min-width: 760px;
}
/* Simula una tabla: columnas proporcionales y celdas con separador */
.bu-row {
  display: grid;
  /* Recibo | Empleado | Estado | Importar */
  grid-template-columns: minmax(200px, 1.3fr) minmax(240px, 1.5fr) minmax(200px, 1fr) 90px;
  align-items: stretch;
}
.bu-row > div {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-right: 1px solid #e3e8ea;
  min-width: 0;
}
.bu-row > div:last-child {
  border-right: none;
  justify-content: center;
}
.bu-row--head {
  background: #f5f8f9;
  border-bottom: 1px solid #c9d3d7;
  font-size: 12px;
  font-weight: 600;
  color: #4a5a62;
}
.bu-row--item {
  border-bottom: 1px solid #e3e8ea;
}
.bu-row--item:hover {
  background: #fafcfc;
}
.bu-row > .bu-file {
  align-items: flex-start;
  justify-content: center;
}
.bu-file {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.bu-file a,
.bu-file-name {
  color: #1f6fb5;
  font-size: 14px;
  font-weight: 500;
  overflow-wrap: anywhere;
}
.bu-file-name {
  color: #1f2d33;
}
.bu-file a:hover {
  text-decoration: underline;
}
.bu-muted {
  font-size: 12px;
  color: #5a6a72;
}
.bu-select {
  font-size: 14px;
  max-width: 360px;
}
.bu-row > .bu-status {
  align-items: flex-start;
  justify-content: center;
}
.bu-status {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}
.bu-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 500;
}
.bu-badge i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.bu-badge--unmatched {
  background: #fff1e0;
  color: #a14400;
}
.bu-badge--unmatched i {
  background: #e65100;
}
.bu-badge--omitted {
  background: #eceff1;
  color: #4a5a62;
}
.bu-badge--omitted i {
  background: #78909c;
}
.bu-badge--ready,
.bu-badge--imported {
  background: #e6f4ea;
  color: #1b5e20;
}
.bu-badge--ready i,
.bu-badge--imported i {
  background: #2e7d32;
}
.bu-badge--error {
  background: #fdecea;
  color: #b71c1c;
}
.bu-badge--error i {
  background: #c62828;
}
.bu-check {
  display: flex;
  align-items: center;
  min-height: 44px;
}
.bu-empty {
  padding: 32px 0;
  text-align: center;
  font-size: 14px;
  color: #5a6a72;
}
</style>
